import React from 'react';
import { Filter, X, Calendar, Activity, Settings } from 'lucide-react';
import { DateRangePicker } from './date-range-picker';
import { MultiSelect } from './multi-select';
import { StatusFilter } from './StatusFilter';
import { Button } from './button';
import { formatManualWeldingBugTypeLabel } from '../../utils/manualWeldingUtils';

export interface WeldSegmentViewFiltersProps {
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  passNameFilter: string[];
  onPassNameFilterChange: (value: string[]) => void;
  bugTypeFilter: string[];
  onBugTypeFilterChange: (value: string[]) => void;
  dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  onDateRangeChange: (value: { dateFrom: Date | undefined; dateTo: Date | undefined }) => void;
  passNameOptions: string[];
  bugTypeOptions: string[];
  totalCount: number;
  filteredCount: number;
}

export const WeldSegmentViewFilters: React.FC<WeldSegmentViewFiltersProps> = ({
  statusFilter,
  onStatusFilterChange,
  passNameFilter,
  onPassNameFilterChange,
  bugTypeFilter,
  onBugTypeFilterChange,
  dateRange,
  onDateRangeChange,
  passNameOptions,
  bugTypeOptions,
  totalCount,
  filteredCount,
}) => {
  const clearAllFilters = () => {
    onStatusFilterChange([]);
    onPassNameFilterChange([]);
    onBugTypeFilterChange([]);
    onDateRangeChange({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters =
    statusFilter.length > 0 ||
    passNameFilter.length > 0 ||
    bugTypeFilter.length > 0 ||
    dateRange.dateFrom ||
    dateRange.dateTo;

  const toOptions = (values: string[]) => values.map((value) => ({ value, label: value }));
  const bugTypeFilterOptions = bugTypeOptions.map((value) => ({
    value,
    label: formatManualWeldingBugTypeLabel(value),
  }));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700 text-xs">Weld Segment Filters</span>
          <span className="text-xs text-gray-500">
            Showing {filteredCount} of {totalCount}
          </span>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
            <X className="h-3 w-3 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
            <Activity className="h-4 w-4" />
            Status
          </label>
          <StatusFilter
            selected={statusFilter}
            onChange={onStatusFilterChange}
            placeholder="Select status"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700">Pass</label>
          <MultiSelect
            options={toOptions(passNameOptions)}
            selected={passNameFilter}
            onChange={onPassNameFilterChange}
            placeholder="All Passes"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
            <Settings className="h-4 w-4" />
            Bug Type
          </label>
          <MultiSelect
            options={bugTypeFilterOptions}
            selected={bugTypeFilter}
            onChange={onBugTypeFilterChange}
            placeholder="All Bug Types"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Date Range
          </label>
          <DateRangePicker
            initialDateFrom={dateRange.dateFrom}
            initialDateTo={dateRange.dateTo}
            onUpdate={(values) => onDateRangeChange({
              dateFrom: values.dateFrom,
              dateTo: values.dateTo,
            })}
          />
        </div>
      </div>
    </div>
  );
};
